import z from 'zod';
import { LegacyExamAttemptStatus, LegacyExamAttemptType } from '../common/enums';
import { GenericTipTapJsonSchema, SubjectSchema, UnixTimestampSchema } from '../common/schemas';
import {
  DBLegacyExamQuestionCriterionIdSchema,
  DBLegacyExamQuestionLabelSchema,
  DBLegacyExamQuestionQuestionIdSchema,
  DBLegacyExamQuestionTypeSchema,
} from '../legacyExamQuestions/schemas';
import {
  DBLegacyExamExamIdSchema,
  DBLegacyExamLabelSchema,
  LegacyExamExamIdSchema,
  LegacyExamSubjectSchema,
} from '../legacyExams/schemas';
import { DBLegacyExamTypeTypeSchema } from '../legacyExamTypes/schemas';
import {
  DBStaffFirstNameSchema,
  DBStaffLastNameSchema,
  DBStaffStaffIdSchema,
} from '../staff/schemas';
import {
  DBStudentFirstNameSchema,
  DBStudentLastNameSchema,
  DBStudentStudentIdSchema,
} from '../students/schemas';
import { LegacyExamQuestionMarkingMode, LegacyExamQuestionMarkOutcome } from './enums';

/**
 * Shared & Helper Schemas
 */

/**
 * DB Schemas
 */

export const DBLegacyExamQuestionAttemptStudentSchema = z.lazy(() => DBStudentStudentIdSchema);
export const DBLegacyExamQuestionAttemptSubjectExamQuestionIdentifierSchema = z
  .string()
  .refine(val => {
    const parts = val.split('#');
    return (
      parts.length === 5 &&
      z.literal(LegacyExamAttemptType.Question).safeParse(parts[0]).success &&
      SubjectSchema.safeParse(parts[1]).success &&
      z.lazy(() => DBLegacyExamExamIdSchema).safeParse(parts[2]).success &&
      z
        .string()
        .regex(/^[0-9]+$/)
        .safeParse(parts[3]).success &&
      DBLegacyExamQuestionQuestionIdSchema.safeParse(parts[4]).success
    );
  });
export const DBLegacyExamQuestionAttemptCreationDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamQuestionAttemptCriteriaSchema = z.record(
  DBLegacyExamQuestionCriterionIdSchema,
  z.boolean()
);
export const DBLegacyExamQuestionAttemptExamSchema = z.string().refine(val => {
  const parts = val.split('#');

  return (
    parts.length === 2 &&
    SubjectSchema.safeParse(parts[0]).success &&
    z.lazy(() => DBLegacyExamExamIdSchema).safeParse(parts[1]).success
  );
});
export const DBLegacyExamQuestionAttemptExamLabelSchema = z.lazy(() => DBLegacyExamLabelSchema);
export const DBLegacyExamQuestionAttemptExamTypeSchema = z.lazy(() => DBLegacyExamTypeTypeSchema);
export const DBLegacyExamQuestionAttemptInaccuracyMarksSchema = z.number().int().min(0);
export const DBLegacyExamQuestionAttemptMarkSchema = z.number().int().min(0);
export const DBLegacyExamQuestionAttemptMarkedDatetimeSchema = UnixTimestampSchema;
export const DBLegacyExamQuestionAttemptMarkerDetailsSchema = z.object({
  firstName: z.lazy(() => DBStaffFirstNameSchema),
  lastName: z.lazy(() => DBStaffLastNameSchema),
});
export const DBLegacyExamQuestionAttemptMarkerIdSchema = z.lazy(() => DBStaffStaffIdSchema);
export const DBLegacyExamQuestionAttemptMarkerCommentSchema = GenericTipTapJsonSchema;
export const DBLegacyExamQuestionAttemptMarkingModeSchema = z.enum(LegacyExamQuestionMarkingMode);
export const DBLegacyExamQuestionAttemptMarksBreakdownSchema = z.array(
  z.enum(LegacyExamQuestionMarkOutcome)
);
export const DBLegacyExamQuestionAttemptMaxMarkSchema = z.number().int().min(0);
export const DBLegacyExamQuestionAttemptMcqOptsSchema = z.object({
  answer: z.number().int(),
  response: z.number().int(),
});
export const DBLegacyExamQuestionAttemptMissedMarksSchema = z.number().int().min(0);
export const DBLegacyExamQuestionAttemptOutcomeSchema = z.number().min(0).max(1);
export const DBLegacyExamQuestionAttemptQuestionSchema = DBLegacyExamQuestionQuestionIdSchema;
export const DBLegacyExamQuestionAttemptQuestionLabelSchema = z.lazy(
  () => DBLegacyExamQuestionLabelSchema
);
export const DBLegacyExamQuestionAttemptSillyMarksSchema = z.number().int().min(0);
export const DBLegacyExamQuestionAttemptStatusSchema = z.string().refine(val => {
  const parts = val.split('#');
  return (
    parts.length === 2 &&
    z.literal(LegacyExamAttemptType.Question).safeParse(parts[0]).success &&
    z.enum(LegacyExamAttemptStatus).safeParse(parts[1]).success
  );
});
export const DBLegacyExamQuestionAttemptStudentDetailsSchema = z.object({
  firstName: z.lazy(() => DBStudentFirstNameSchema),
  lastName: z.lazy(() => DBStudentLastNameSchema),
});
export const DBLegacyExamQuestionAttemptTypeSchema = z.lazy(() => DBLegacyExamQuestionTypeSchema);

export const DBLegacyExamQuestionAttemptSchema = z.object({
  student: DBLegacyExamQuestionAttemptStudentSchema,
  subjectExamQuestionIdentifier: DBLegacyExamQuestionAttemptSubjectExamQuestionIdentifierSchema,
  creationDatetime: DBLegacyExamQuestionAttemptCreationDatetimeSchema,
  criteria: DBLegacyExamQuestionAttemptCriteriaSchema.optional(),
  exam: DBLegacyExamQuestionAttemptExamSchema,
  examLabel: DBLegacyExamQuestionAttemptExamLabelSchema,
  examType: DBLegacyExamQuestionAttemptExamTypeSchema,
  inaccuracyMarks: DBLegacyExamQuestionAttemptInaccuracyMarksSchema,
  mark: DBLegacyExamQuestionAttemptMarkSchema.optional(),
  // Optional — undefined means not yet marked
  markedDatetime: DBLegacyExamQuestionAttemptMarkedDatetimeSchema.optional(),
  markerId: DBLegacyExamQuestionAttemptMarkerIdSchema,
  markerComment: DBLegacyExamQuestionAttemptMarkerCommentSchema.optional(),
  markerDetails: DBLegacyExamQuestionAttemptMarkerDetailsSchema,
  markingMode: DBLegacyExamQuestionAttemptMarkingModeSchema,
  marksBreakdown: DBLegacyExamQuestionAttemptMarksBreakdownSchema.optional(),
  maxMark: DBLegacyExamQuestionAttemptMaxMarkSchema,
  mcqOpts: DBLegacyExamQuestionAttemptMcqOptsSchema.optional(),
  missedMarks: DBLegacyExamQuestionAttemptMissedMarksSchema,
  outcome: DBLegacyExamQuestionAttemptOutcomeSchema,
  questionLabel: DBLegacyExamQuestionAttemptQuestionLabelSchema,
  question: DBLegacyExamQuestionAttemptQuestionSchema,
  sillyMarks: DBLegacyExamQuestionAttemptSillyMarksSchema,
  status: DBLegacyExamQuestionAttemptStatusSchema,
  studentDetails: DBLegacyExamQuestionAttemptStudentDetailsSchema,
  type: DBLegacyExamQuestionAttemptTypeSchema,
});

/**
 * Client Schemas
 */

export const LegacyExamQuestionAttemptStudentSchema = DBLegacyExamQuestionAttemptStudentSchema;
export const LegacyExamQuestionAttemptExamSubjectSchema = z.lazy(() => LegacyExamSubjectSchema);
export const LegacyExamQuestionAttemptExamIdSchema = z.lazy(() => LegacyExamExamIdSchema);
export const LegacyExamQuestionAttemptExamAttemptNumberSchema = z.number().int().min(1);
export const LegacyExamQuestionAttemptQuestionIdSchema = z.lazy(
  () => DBLegacyExamQuestionQuestionIdSchema
);
export const LegacyExamQuestionAttemptCreationDatetimeSchema =
  DBLegacyExamQuestionAttemptCreationDatetimeSchema;
export const LegacyExamQuestionAttemptCriteriaSchema =
  DBLegacyExamQuestionAttemptCriteriaSchema.optional();
export const LegacyExamQuestionAttemptExamLabelSchema = DBLegacyExamQuestionAttemptExamLabelSchema;
export const LegacyExamQuestionAttemptExamTypeSchema = DBLegacyExamQuestionAttemptExamTypeSchema;
export const LegacyExamQuestionAttemptInaccuracyMarksSchema =
  DBLegacyExamQuestionAttemptInaccuracyMarksSchema;
export const LegacyExamQuestionAttemptMarkSchema = DBLegacyExamQuestionAttemptMarkSchema;
export const LegacyExamQuestionAttemptMarkedDatetimeSchema =
  DBLegacyExamQuestionAttemptMarkedDatetimeSchema;
export const LegacyExamQuestionAttemptMarkerDetailsSchema =
  DBLegacyExamQuestionAttemptMarkerDetailsSchema;
export const LegacyExamQuestionAttemptMarkerIdSchema = DBLegacyExamQuestionAttemptMarkerIdSchema;
export const LegacyExamQuestionAttemptMarkerCommentSchema =
  DBLegacyExamQuestionAttemptMarkerCommentSchema;
export const LegacyExamQuestionAttemptMarkingModeSchema =
  DBLegacyExamQuestionAttemptMarkingModeSchema;
export const LegacyExamQuestionAttemptMarksBreakdownSchema =
  DBLegacyExamQuestionAttemptMarksBreakdownSchema;
export const LegacyExamQuestionAttemptMaxMarkSchema = DBLegacyExamQuestionAttemptMaxMarkSchema;
export const LegacyExamQuestionAttemptMcqOptsSchema = DBLegacyExamQuestionAttemptMcqOptsSchema;
export const LegacyExamQuestionAttemptMissedMarksSchema =
  DBLegacyExamQuestionAttemptMissedMarksSchema;
export const LegacyExamQuestionAttemptOutcomeSchema = DBLegacyExamQuestionAttemptOutcomeSchema;
export const LegacyExamQuestionAttemptQuestionSchema = DBLegacyExamQuestionAttemptQuestionSchema;
export const LegacyExamQuestionAttemptQuestionLabelSchema =
  DBLegacyExamQuestionAttemptQuestionLabelSchema;
export const LegacyExamQuestionAttemptSillyMarksSchema =
  DBLegacyExamQuestionAttemptSillyMarksSchema;
export const LegacyExamQuestionAttemptStatusSchema = z.enum(LegacyExamAttemptStatus);
export const LegacyExamQuestionAttemptStudentDetailsSchema =
  DBLegacyExamQuestionAttemptStudentDetailsSchema;
export const LegacyExamQuestionAttemptTypeSchema = DBLegacyExamQuestionAttemptTypeSchema;

export const LegacyExamQuestionAttemptSchema = z.object({
  student: LegacyExamQuestionAttemptStudentSchema,
  examSubject: LegacyExamQuestionAttemptExamSubjectSchema,
  examId: LegacyExamQuestionAttemptExamIdSchema,
  examAttemptNumber: LegacyExamQuestionAttemptExamAttemptNumberSchema,
  questionId: LegacyExamQuestionAttemptQuestionIdSchema,
  creationDatetime: LegacyExamQuestionAttemptCreationDatetimeSchema,
  criteria: LegacyExamQuestionAttemptCriteriaSchema.optional(),
  examLabel: LegacyExamQuestionAttemptExamLabelSchema,
  examType: LegacyExamQuestionAttemptExamTypeSchema,
  // Default to 0 — not populated until the question attempt is marked
  inaccuracyMarks: LegacyExamQuestionAttemptInaccuracyMarksSchema.default(0),
  mark: LegacyExamQuestionAttemptMarkSchema.optional(),
  // Optional — undefined means not yet marked
  markedDatetime: LegacyExamQuestionAttemptMarkedDatetimeSchema.optional(),
  markerId: LegacyExamQuestionAttemptMarkerIdSchema,
  markerDetails: LegacyExamQuestionAttemptMarkerDetailsSchema,
  markerComment: LegacyExamQuestionAttemptMarkerCommentSchema.optional(),
  markingMode: LegacyExamQuestionAttemptMarkingModeSchema,
  marksBreakdown: LegacyExamQuestionAttemptMarksBreakdownSchema.optional(),
  maxMark: LegacyExamQuestionAttemptMaxMarkSchema,
  mcqOpts: LegacyExamQuestionAttemptMcqOptsSchema.optional(),
  missedMarks: LegacyExamQuestionAttemptMissedMarksSchema.default(0),
  outcome: LegacyExamQuestionAttemptOutcomeSchema,
  question: LegacyExamQuestionAttemptQuestionSchema,
  questionLabel: LegacyExamQuestionAttemptQuestionLabelSchema,
  sillyMarks: LegacyExamQuestionAttemptSillyMarksSchema.default(0),
  status: LegacyExamQuestionAttemptStatusSchema,
  studentDetails: LegacyExamQuestionAttemptStudentDetailsSchema,
  type: LegacyExamQuestionAttemptTypeSchema,
});
